/** @jsx jsx */
import IconChevronDown from '@apollo/icons/default/IconChevronDown.svg';
import IconChevronUp from '@apollo/icons/default/IconChevronUp.svg';
import IconCopy from '@apollo/icons/default/IconCopy.svg';
import IconDownload from '@apollo/icons/default/IconDownload.svg';
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  Button,
  IconButton,
} from '@apollo/orbit';
import { css, jsx } from '@emotion/react';
import classnames from 'classnames';
import copy from 'copy-to-clipboard';
import { saveAs } from 'file-saver';
import { get, orderBy, truncate, uniq } from 'lodash';
import React, { useMemo } from 'react';

import {
  JSONObject,
  JSONValue,
  isJSONObject,
  isJSONPrimitive,
} from 'src/app/graph/explorerPage/helpers/types';
import { Tooltip } from 'src/components/common/tooltip/Tooltip';
import { isMinLengthArray } from 'src/lib/isMinLengthArray';

import { stringifyCSV } from './stringifyCSV';
interface JSONBreadcrumbProps {
  path: string[];
  setPath: React.Dispatch<React.SetStateAction<string[]>>;
}

const JSONBreadcrumb = ({ path, setPath }: JSONBreadcrumbProps) => (
  <Breadcrumb
    className="mb-2 mt-1 flex flex-wrap items-center"
    separatorType="chevron"
    css={css`
      ol {
        flex-wrap: wrap;
      }
    `}
  >
    {path.map((segment, i) => (
      <BreadcrumbItem
        className="mt-1 cursor-pointer hover:underline"
        key={path.slice(0, i + 1).join('>')}
        isCurrentPage={i === path.length - 1}
        onClick={() => setPath(path.slice(0, i + 1))}
      >
        <BreadcrumbLink className="text-sm font-normal">
          {segment}
        </BreadcrumbLink>
      </BreadcrumbItem>
    ))}
  </Breadcrumb>
);

const PREVIEW_MAX_ROWS = 5;
const PREVIEW_STRING_LENGTH = 40;

interface JSONTableProps {
  jsonValue: JSONValue;
  path: string[];
  setPath: React.Dispatch<React.SetStateAction<string[]>>;
  nesting?: number;
}
const JSONTable = ({
  jsonValue,
  path,
  setPath,
  nesting = 0,
}: JSONTableProps) => {
  const [valueExpanded, setValueExpanded] = React.useState(false);

  const [sortedColumn, setSortedColumn] = React.useState<SortCriteria | null>();
  React.useEffect(() => setSortedColumn(null), [path]);
  const sortedJSONValue: JSONValue = React.useMemo(
    () =>
      Array.isArray(jsonValue) && sortedColumn
        ? // Typescript cant handle the way lodash generics work with infinitely
          // nested types, simplify it here.
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          orderBy<any>(jsonValue, sortedColumn.name, sortedColumn.order)
        : jsonValue,
    [sortedColumn, jsonValue],
  );

  if (
    nesting > 5 ||
    typeof sortedJSONValue !== 'object' ||
    sortedJSONValue === null
  ) {
    const stringValue =
      typeof sortedJSONValue === 'object'
        ? JSON.stringify(sortedJSONValue)
        : sortedJSONValue.toString();
    if (valueExpanded || stringValue.length <= PREVIEW_STRING_LENGTH) {
      return (
        <div
          className={classnames(
            'px-2 py-1 text-code-d',
            sortedJSONValue === null && 'font-mono text-sm',
          )}
        >
          {stringValue}
        </div>
      );
    }
    return (
      <Button
        size="sm"
        variant="hidden"
        className="w-full justify-start rounded-none font-normal text-code-d"
        title={stringValue}
        onClick={() => setValueExpanded(true)}
      >
        {valueExpanded
          ? stringValue
          : truncate(stringValue, {
              length: PREVIEW_STRING_LENGTH,
            })}
      </Button>
    );
  }

  // Empty list
  if (Array.isArray(sortedJSONValue) && sortedJSONValue.length === 0) {
    return <div className="px-2 text-code-a">[]</div>;
  }

  // Object
  if (!Array.isArray(sortedJSONValue)) {
    return (
      <table className="xxx-json-table-hover w-full text-left text-sm">
        <tbody>
          {Object.keys(sortedJSONValue).map((key, i) => {
            const value = sortedJSONValue?.[key];
            if (
              !value ||
              typeof value !== 'object' ||
              (Array.isArray(value) && value.length === 0)
            ) {
              return (
                <tr
                  className={classnames(
                    'border-primary',
                    i !== 0 && 'border-t',
                  )}
                  key={[...path, i.toString()].join('.')}
                >
                  <th className="p-0">
                    <Button
                      variant="hidden"
                      className="w-full justify-start rounded-none font-mono font-normal text-code-b"
                      size="sm"
                      onClick={() => setPath([...path, key])}
                    >
                      {key}
                    </Button>
                  </th>
                  <td className="border-l border-primary p-0">
                    {value === undefined ? (
                      <div className="px-2 font-mono text-code-a">-</div>
                    ) : (
                      <JSONTable
                        jsonValue={value}
                        path={[...path, key]}
                        setPath={setPath}
                        nesting={nesting + 1}
                      />
                    )}
                  </td>
                </tr>
              );
            }

            return (
              <React.Fragment key={[...path, i.toString()].join('.')}>
                <tr
                  className={classnames(
                    'border-primary',
                    i !== 0 && 'border-t',
                  )}
                >
                  <th className="relative p-0" colSpan={2}>
                    <Button
                      className="group w-full items-center justify-start rounded-none font-mono font-normal text-code-b"
                      variant="hidden"
                      size="sm"
                      onClick={() => setPath([...path, key])}
                    >
                      {key}
                    </Button>
                    {Array.isArray(value) && (
                      <Tooltip label="Download section as CSV">
                        <Button
                          className="absolute right-1 top-1"
                          size="xs"
                          variant="hidden"
                          title={`Download "${key}" as CSV`}
                          onClick={(e) => {
                            e.stopPropagation();
                            downloadCsv({ fileName: key, value });
                          }}
                          leftIcon={<IconDownload />}
                        >
                          CSV
                        </Button>
                      </Tooltip>
                    )}
                  </th>
                </tr>
                <tr className="border-t border-primary">
                  <td />
                  <td className="border-l border-primary p-0">
                    <JSONTable
                      jsonValue={value}
                      path={[...path, key]}
                      setPath={setPath}
                      nesting={nesting + 1}
                    />
                  </td>
                </tr>
              </React.Fragment>
            );
          })}
        </tbody>
      </table>
    );
  }

  // List of leaf nodes
  if (
    typeof sortedJSONValue[0] !== 'object' ||
    Array.isArray(sortedJSONValue[0])
  ) {
    const truncatedRows =
      nesting > 0 && !valueExpanded
        ? sortedJSONValue.slice(0, PREVIEW_MAX_ROWS - 1)
        : sortedJSONValue;
    return (
      <table className="w-full text-left">
        <tbody>
          {truncatedRows.map((row, i) => {
            return (
              <tr
                className={classnames(
                  'border-primary align-top',
                  i !== 0 && 'border-t',
                )}
                key={[...path, i.toString()].join('.')}
              >
                <th className="relative p-0" style={{ width: 1 }}>
                  <Button
                    className="w-full justify-start rounded-none align-top font-normal text-code-g"
                    size="sm"
                    variant="hidden"
                    onClick={() => setPath([...path, i.toString()])}
                  >
                    {i}
                  </Button>
                </th>
                <td className="border-l border-primary p-0">
                  <JSONTable
                    jsonValue={row}
                    path={[...path, i.toString()]}
                    setPath={setPath}
                    nesting={nesting + 1}
                  />
                </td>
              </tr>
            );
          })}
          {sortedJSONValue.length !== truncatedRows.length && (
            <tr className="border-t border-primary" key="expand">
              <td colSpan={2} className="p-0">
                <Button
                  size="sm"
                  variant="hidden"
                  className="w-full justify-start rounded-none font-normal text-code-g"
                  onClick={() => setValueExpanded(true)}
                  rightIcon={<IconChevronDown />}
                >
                  Reveal {sortedJSONValue.length - truncatedRows.length} rows
                </Button>
              </td>
            </tr>
          )}
        </tbody>
      </table>
    );
  }

  // List of objects
  const fullRows = sortedJSONValue.filter(
    (row): row is JSONObject | null => typeof row === 'object',
  );
  const rows =
    nesting > 0 && !valueExpanded
      ? fullRows.slice(0, PREVIEW_MAX_ROWS - 1)
      : fullRows;
  const keys = uniq(rows.flatMap((row) => (row ? Object.keys(row) : [])));

  return (
    <table className="w-full">
      <tbody>
        <tr>
          {keys.map((key) => {
            const currentSort =
              sortedColumn?.name === key ? sortedColumn.order : null;
            // Can only sort scalars
            const shouldShowSort = Boolean(
              sortedJSONValue[0] &&
                typeof sortedJSONValue[0] === 'object' &&
                !Array.isArray(sortedJSONValue[0]) &&
                typeof sortedJSONValue[0][key] !== 'object',
            );
            return (
              <th className="p-0" key={key} colSpan={2}>
                <Tooltip
                  label={
                    sortedJSONValue.length === 0
                      ? 'Nothing to sort: List length is 1'
                      : currentSort === 'desc'
                        ? 'Stop sorting'
                        : currentSort === 'asc'
                          ? 'Sort descending'
                          : 'Sort ascending'
                  }
                  disabled={sortedJSONValue.length === 1}
                >
                  <Button
                    size="sm"
                    variant="hidden"
                    className="group w-full justify-start rounded-none font-normal text-code-b"
                    onClick={() =>
                      shouldShowSort
                        ? setSortedColumn(
                            currentSort === 'desc'
                              ? null
                              : currentSort === 'asc'
                                ? { name: key, order: 'desc' }
                                : { name: key, order: 'asc' },
                          )
                        : setPath(path)
                    }
                    rightIcon={
                      !shouldShowSort ? undefined : !currentSort ? (
                        <IconChevronDown className="invisible group-hover:visible" />
                      ) : currentSort === 'asc' ? (
                        <IconChevronDown />
                      ) : (
                        <IconChevronUp />
                      )
                    }
                  >
                    {key}
                  </Button>
                </Tooltip>
              </th>
            );
          })}
        </tr>
        {rows.map((row, i) => {
          return (
            <tr
              className="border-t border-primary"
              key={[...path, i.toString()].join('.')}
            >
              <th className="p-0 align-top" style={{ width: 1 }}>
                <Button
                  size="sm"
                  variant="hidden"
                  className="w-full justify-start rounded-none font-normal text-code-g"
                  onClick={() => setPath([...path, i.toString()])}
                >
                  {i}
                </Button>
              </th>
              {keys.map((key) => {
                const value = row?.[key];
                return (
                  <td className="border-l border-primary p-0" key={key}>
                    {value !== undefined && value !== null ? (
                      <JSONTable
                        jsonValue={value}
                        path={[...path, i.toString(), key]}
                        setPath={setPath}
                        nesting={nesting + 1}
                      />
                    ) : row ? (
                      '-'
                    ) : null}
                  </td>
                );
              })}
            </tr>
          );
        })}
        {fullRows.length !== rows.length && (
          <tr className="border-t border-primary" key="expand">
            <td colSpan={keys.length + 1} className="p-0">
              <Button
                size="sm"
                variant="hidden"
                className="w-full justify-start rounded-none font-normal text-code-g"
                onClick={() => setValueExpanded(true)}
                rightIcon={<IconChevronDown />}
              >
                Reveal {fullRows.length - rows.length} rows
              </Button>
            </td>
          </tr>
        )}
      </tbody>
    </table>
  );
};

function getStartingPath(
  json: JSONObject,
  path: string[] = ['Root'],
): string[] {
  // If this is the root, ignore extensions: in the common case of getting
  // exactly data and extensions, the user probably wants to look at the data.
  // (They can navigate back to the root explicitly if they want to see extensions.)
  const keys =
    path.length === 1
      ? Object.keys(json).filter((key) => key !== 'extensions')
      : Object.keys(json);
  if (keys.length <= 1 && isMinLengthArray(1, keys)) {
    const value = json[keys[0]];
    if (value && typeof value === 'object') {
      if (Array.isArray(value)) {
        return [...path, keys[0]];
      }
      return getStartingPath(value, [...path, keys[0]]);
    }
  }
  return path;
}

interface SortCriteria {
  name: string;
  order: 'asc' | 'desc';
}

interface JSONTableViewProps {
  json: JSONObject;
  _path?: string[];
  className?: string;
}
export const JSONTableView = ({
  json,
  _path,
  className,
}: JSONTableViewProps) => {
  const [path, setPath] = React.useState(() => _path ?? getStartingPath(json));
  React.useEffect(() => {
    setPath(_path ?? getStartingPath(json));
  }, [json, _path]);

  const currentJSON = useMemo(() => {
    const unflattenedJson: JSONValue =
      path.length > 1 ? get(json, path.slice(1), null) : json;

    const canBeFlattened =
      Array.isArray(unflattenedJson) &&
      unflattenedJson.every(
        (value): value is JSONObject =>
          isJSONObject(value) &&
          Object.values(value).every(
            (v) =>
              isJSONPrimitive(v) ||
              (isJSONObject(v) && Object.values(v).every(isJSONPrimitive)),
          ),
      );
    if (canBeFlattened) {
      return unflattenedJson.map((arrayValue) =>
        Object.fromEntries(
          Object.entries(arrayValue).flatMap(([key, value]) =>
            isJSONObject(value)
              ? Object.entries(value).map(([k, v]) => [`${key}.${k}`, v])
              : [[key, value]],
          ),
        ),
      );
    } else {
      return unflattenedJson;
    }
  }, [json, path]);

  return (
    <div
      className={classnames(className, 'flex flex-col text-sm text-primary')}
    >
      <div className="flex">
        <JSONBreadcrumb path={path} setPath={setPath} />
        <div className="ml-auto mt-2 flex gap-1">
          <Tooltip label="Copy response">
            <IconButton
              aria-label="Copy JSON to clipboard"
              size="sm"
              variant="hidden"
              onClick={() => copy(JSON.stringify(currentJSON, null, '  '))}
              icon={<IconCopy />}
            />
          </Tooltip>
          {Array.isArray(currentJSON) && (
            <Tooltip label="Download contents as CSV">
              <Button
                size="sm"
                variant="hidden"
                onClick={(e) => {
                  e.stopPropagation();
                  downloadCsv({
                    fileName: path.slice(-1)[0] ?? 'data',
                    value: currentJSON,
                  });
                }}
                leftIcon={<IconDownload />}
              >
                CSV
              </Button>
            </Tooltip>
          )}
          <Tooltip label="Download contents as JSON">
            <Button
              size="sm"
              variant="hidden"
              as="a"
              leftIcon={<IconDownload />}
              download="response.json"
              href={`data:text/json;charset=utf-8,${encodeURIComponent(
                JSON.stringify(currentJSON),
              )}`}
            >
              JSON
            </Button>
          </Tooltip>
        </div>
      </div>
      <div className="overflow-auto">
        <div className="table w-full border border-primary">
          <JSONTable jsonValue={currentJSON} path={path} setPath={setPath} />
        </div>
      </div>
    </div>
  );
};

async function downloadCsv({
  fileName,
  value,
}: {
  fileName: string;
  value: unknown[];
}) {
  saveAs(
    new Blob(
      [
        new Uint8Array([0xef, 0xbb, 0xbf]), // UTF-8 BOM to tell Excel 2007+ to treat this file as unicode
        await stringifyCSV({
          fileName,
          inputArray: value,
        }),
      ],
      {
        type: 'text/csv;charset=utf-8',
      },
    ),
    `${fileName}.csv`,
  );
}
